import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { useToasts } from '../../../../context/Toast';
import { useNamespacedTranslation } from '../../../../hooks/useNamespacedTranslation';
import {
  ApprovalRequestStatus,
  CompensationReview_ReviewFragment,
  CompensationReviewItem_ItemFragment,
  CompensationReviewListItem_ReviewFragment,
  useApplyCompensationMeritMutation,
  useCompensationReviewQuery,
  useCompensationReviewsQuery,
  useDecideCompensationReviewMutation,
  useSubmitCompensationReviewMutation,
  useUpdateCompensationReviewItemMutation,
} from '../../__generated__/graphql';
import {
  CompensationReview,
  CompensationReviewItem,
  PayFrequency,
  ReviewRole,
  ReviewStatus,
} from './types';

// ---------------------------------------------------------------------------
// GraphQL -> domain mapping. The components and `logic.ts` consume the stable
// `types.ts` shapes; only this file knows about the generated GraphQL types.
// ---------------------------------------------------------------------------

const mapItem = (
  item: CompensationReviewItem_ItemFragment,
): CompensationReviewItem => ({
  id: item.Id,
  employee: {
    id: item.employee.Id,
    name: item.employee.Name,
  },
  position: item.position ?? '',
  frequency: String(item.frequency) as PayFrequency,
  referenceAmount: item.flair__Reference_Amount__c ?? 0,
  newAmount: item.flair__New_Amount__c ?? 0,
  payBand: item.payBand
    ? { min: item.payBand.min, max: item.payBand.max }
    : null,
  included: item.flair__Included__c,
});

const mapReview = (
  review: CompensationReview_ReviewFragment,
): CompensationReview => ({
  id: review.Id,
  name: review.Name,
  // Don't coerce an unrecognised status to 'PREPARING' — that would wrongly
  // enable editing. It passes through as 'UNKNOWN' (read-only).
  status: String(review.flair__Status__c) as ReviewStatus,
  referenceDate: review.flair__Reference_Date__c ?? '',
  effectiveDate: review.flair__Effective_Date__c ?? '',
  assessingManager: {
    id: review.assessingManager.Id,
    name: review.assessingManager.Name,
  },
  currency: review.currencyIsoCode,
  myRole: String(review.myRole) as ReviewRole,
  approvalRequestId: review.myApprovalRequest?.Id ?? null,
  items: review.items.map(mapItem),
});

// Header-only mapping for the list (no line items are fetched there).
const mapReviewListItem = (
  review: CompensationReviewListItem_ReviewFragment,
): CompensationReview => ({
  id: review.Id,
  name: review.Name,
  status: String(review.flair__Status__c) as ReviewStatus,
  referenceDate: review.flair__Reference_Date__c ?? '',
  effectiveDate: review.flair__Effective_Date__c ?? '',
  assessingManager: {
    id: review.assessingManager.Id,
    name: review.assessingManager.Name,
  },
  currency: review.currencyIsoCode,
  myRole: String(review.myRole) as ReviewRole,
  approvalRequestId: review.myApprovalRequest?.Id ?? null,
  items: [],
});

export const useCompensationReviewList = (): {
  reviews: CompensationReview[];
  loading: boolean;
  error?: ApolloError;
} => {
  const { data, loading, error } = useCompensationReviewsQuery();
  const reviews = useMemo(
    () => (data?.me.compensationReviews ?? []).map(mapReviewListItem),
    [data],
  );
  return { reviews, loading, error };
};

type DetailActions = {
  setNewAmount: (itemId: string, amount: number) => void;
  setIncluded: (itemId: string, included: boolean) => void;
  applyMeritToAll: (meritPercent: number) => void;
  submitForApproval: () => Promise<void>;
  approve: () => Promise<void>;
  reject: () => Promise<void>;
  isProcessing: boolean;
};

const DEBOUNCE_MS = 600;

// Detail hook: holds an editable local copy of the review (instant UX) seeded
// from the query, persisting amount edits (debounced) and include toggles to
// Salesforce. Workflow actions await their mutation and refetch.
export const useCompensationReviewDetail = (
  id: string,
): {
  review: CompensationReview | undefined;
  loading: boolean;
  error?: ApolloError;
  actions: DetailActions;
} => {
  const { addSuccess, addError } = useToasts();
  const t = useNamespacedTranslation('compensationReviews.toasts');

  const { data, loading, error, refetch } = useCompensationReviewQuery({
    variables: { id },
  });
  const serverReview = data?.me.compensationReview ?? null;
  const approvalRequestId = serverReview?.myApprovalRequest?.Id ?? null;

  const [review, setReview] = useState<CompensationReview | undefined>(
    undefined,
  );

  // Re-seed local state whenever the server data (or id) changes. Guard on the
  // id: Apollo keeps the previous review's data while the query for a new id is
  // in flight, so without this the old (editable) review could be shown — and
  // edited — against the wrong id. Reset to undefined until the server data
  // actually matches the requested id (the page then shows the skeleton).
  useEffect(() => {
    setReview(
      serverReview && serverReview.Id === id
        ? mapReview(serverReview)
        : undefined,
    );
  }, [serverReview, id]);

  const [updateItem] = useUpdateCompensationReviewItemMutation();
  const [applyMeritMutation] = useApplyCompensationMeritMutation();
  const [submitMutation] = useSubmitCompensationReviewMutation();
  const [decideMutation] = useDecideCompensationReviewMutation();
  const [isProcessing, setIsProcessing] = useState(false);

  // Amount edits are persisted debounced. We flush (persist + await) any pending
  // save before submit and on unmount, so submitting or leaving the page never
  // drops an edit the user already sees locally.
  const timers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pendingAmounts = useRef<Record<string, number>>({});
  const flushPendingAmountSaves = useRef<() => Promise<unknown>>(() =>
    Promise.resolve(),
  );
  flushPendingAmountSaves.current = () => {
    const pending = Object.entries(pendingAmounts.current);
    pending.forEach(([itemId]) => clearTimeout(timers.current[itemId]));
    timers.current = {};
    pendingAmounts.current = {};
    return Promise.all(
      pending.map(([itemId, amount]) =>
        updateItem({
          variables: {
            input: { id: itemId, newAmount: amount, included: null },
          },
        }),
      ),
    );
  };

  useEffect(
    () => () => {
      flushPendingAmountSaves.current().catch(() => undefined);
    },
    [],
  );

  const patchItem = useCallback(
    (itemId: string, patch: Partial<CompensationReviewItem>) =>
      setReview((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) =>
                item.id === itemId ? { ...item, ...patch } : item,
              ),
            }
          : prev,
      ),
    [],
  );

  const setNewAmount = useCallback(
    (itemId: string, amount: number) => {
      patchItem(itemId, { newAmount: amount });
      pendingAmounts.current[itemId] = amount;
      clearTimeout(timers.current[itemId]);
      timers.current[itemId] = setTimeout(() => {
        delete timers.current[itemId];
        delete pendingAmounts.current[itemId];
        updateItem({
          variables: {
            input: { id: itemId, newAmount: amount, included: null },
          },
        }).catch(() => addError(t('actionError')));
      }, DEBOUNCE_MS);
    },
    [patchItem, updateItem, addError, t],
  );

  const setIncluded = useCallback(
    (itemId: string, included: boolean) => {
      patchItem(itemId, { included });
      updateItem({
        variables: { input: { id: itemId, included, newAmount: null } },
      }).catch(() => addError(t('actionError')));
    },
    [patchItem, updateItem, addError, t],
  );

  const run = useCallback(
    (action: () => Promise<unknown>, successKey: string, errorKey: string) => {
      setIsProcessing(true);
      return action()
        .then(() => refetch())
        .then(() => addSuccess(t(successKey)))
        .catch(() => addError(t(errorKey)))
        .then(() => setIsProcessing(false))
        .then(() => undefined);
    },
    [refetch, addSuccess, addError, t],
  );

  // Guard the missing-id case so a decision mutation never fires with an empty
  // id, even though the action UI is already hidden when there's no request.
  const decide = useCallback(
    (status: ApprovalRequestStatus, successKey: string) =>
      run(
        () =>
          approvalRequestId
            ? decideMutation({
                variables: {
                  input: { id: approvalRequestId, status, comment: null },
                },
              })
            : Promise.reject(new Error('No approval request to decide on.')),
        successKey,
        'actionError',
      ),
    [run, approvalRequestId, decideMutation],
  );

  const actions: DetailActions = {
    setNewAmount,
    setIncluded,
    applyMeritToAll: (meritPercent: number) => {
      // Cancel any pending debounced per-row saves so a stale pre-merit amount
      // can't fire after the merit apply (or be flushed on unmount) and
      // overwrite one row's new value.
      Object.values(timers.current).forEach(clearTimeout);
      timers.current = {};
      pendingAmounts.current = {};
      // Optimistic local recompute for instant feedback.
      setReview((prev) =>
        prev
          ? {
              ...prev,
              items: prev.items.map((item) => ({
                ...item,
                newAmount:
                  Math.round(
                    item.referenceAmount * (1 + meritPercent / 100) * 100,
                  ) / 100,
              })),
            }
          : prev,
      );
      setIsProcessing(true);
      applyMeritMutation({
        variables: { input: { reviewId: id, meritPercent } },
      })
        .catch(() => addError(t('actionError')))
        // Re-sync with the server either way: it is the source of truth on
        // success, and on failure the refetch discards the optimistic recompute
        // so the table never disagrees with Salesforce.
        .then(() => refetch())
        .catch(() => undefined)
        .then(() => setIsProcessing(false));
    },
    submitForApproval: () =>
      run(
        // Flush any pending debounced amount edits and await them first, so we
        // never submit (and refetch) over an in-flight save and lose it.
        () =>
          flushPendingAmountSaves
            .current()
            .then(() => submitMutation({ variables: { reviewId: id } })),
        'submitSuccess',
        'submitError',
      ),
    approve: () => decide(ApprovalRequestStatus.Approved, 'approveSuccess'),
    reject: () => decide(ApprovalRequestStatus.Rejected, 'rejectSuccess'),
    isProcessing,
  };

  return { review, loading, error, actions };
};
